import type { BatchedMesh, Box3, Ray } from "three";
import { INTERSECTED, NOT_INTERSECTED, ObjectBVH } from "three-mesh-bvh";

/**
 * A bounds tree over one `BatchedMesh`'s INSTANCES — the part level of the
 * pick index (2026-10-08). `boundsTreeRaycast.ts`'s batched raycast asks it for
 * the instances a ray can reach, and raycasts only those, each through its
 * source geometry's own tree.
 *
 * Without it every pick visits every instance of every batch: on Broach Cell
 * (8,852 batched parts in 29 batches) that loop was 2.1 ms of a 3.3 ms pick.
 * In-page, a tree per batch took a pick from 1.39 to 0.51 ms with identical
 * results, for about 68 bytes a part (0.57 MB) and 9 ms to build.
 *
 * The tree holds each instance's bounds in the BATCH's own frame, so moving
 * the whole model needs nothing. Moving PARTS (explode, a drag, a scene
 * transition) changes instance matrices, which every `setMatrixAt` records by
 * bumping the batch's matrix texture version. While that version keeps
 * changing, the parts are moving: `collect` returns false and the caller tests
 * every instance, exactly as before, because refitting on every probe of a
 * moving model would cost more than the loop it replaces. Once the version has
 * held still for `K_SETTLE_MS`, the next pick refits (5 ms for all of Broach
 * Cell) and the tree is used again.
 */

/** How long parts must be still before the tree is refitted and trusted. */
const K_SETTLE_MS = 250;

/**
 * Outward padding of every instance's bounds, relative to their magnitude:
 * four half-steps of a 32-bit float. Node bounds are stored as 32-bit floats,
 * and the library pads them on a BUILD but not on a refit, so a refitted bound
 * could round inward and cull a ray that only grazes a part's outer face.
 */
const K_BOUNDS_PAD = 2 ** -22;

interface PrimitiveBoundsWriter {
  writePrimitiveBounds(index: number, target: Float32Array, offset: number): Float32Array;
}

const s_baseWriteBounds = (ObjectBVH.prototype as unknown as PrimitiveBoundsWriter).writePrimitiveBounds;

/** `ObjectBVH` whose instance bounds are padded on build AND on refit. */
class PaddedPartTree extends ObjectBVH {
  writePrimitiveBounds(index: number, target: Float32Array, offset: number): Float32Array {
    s_baseWriteBounds.call(this, index, target, offset);
    for (let axis = 0; axis < 3; axis++) {
      const min = target[offset + axis]!;
      const max = target[offset + axis + 3]!;
      const pad = (Math.abs(min) + Math.abs(max)) * K_BOUNDS_PAD;
      target[offset + axis] = min - pad;
      target[offset + axis + 3] = max + pad;
    }
    return target;
  }
}

export class BatchPartIndex {
  private m_tree: PaddedPartTree | null = null;
  private m_treeVersion = -1;
  private m_treeInstances = -1;
  private m_seenVersion = -1;
  private m_seenInstances = -1;
  private m_seenAt = 0;

  constructor(private readonly m_batch: BatchedMesh) {}

  /**
   * Writes into `out`, in ascending id order (three's own order), every
   * instance whose bounds `localRay` crosses — the ray in the batch's local
   * frame. Returns false, leaving `out` empty, when the tree cannot be trusted
   * yet and the caller must test every instance.
   */
  collect(localRay: Ray, out: number[]): boolean {
    out.length = 0;
    const tree = this.currentTree();
    if (!tree) return false;
    tree.shapecast({
      intersectsBounds: (box: Box3) => (localRay.intersectsBox(box) ? INTERSECTED : NOT_INTERSECTED),
      intersectsObject: (_object, instanceId) => {
        out.push(instanceId);
      },
    });
    out.sort((a, b) => a - b);
    return true;
  }

  private currentTree(): PaddedPartTree | null {
    const version = instanceMatricesVersion(this.m_batch);
    if (version === null) return null;
    const instances = this.m_batch.instanceCount;
    if (this.m_tree && this.m_treeVersion === version && this.m_treeInstances === instances) {
      return this.m_tree;
    }
    const now = performance.now();
    if (version !== this.m_seenVersion || instances !== this.m_seenInstances) {
      // Moved (or new) since the last look: wait until it holds still.
      this.m_seenVersion = version;
      this.m_seenInstances = instances;
      this.m_seenAt = now;
      return null;
    }
    if (now - this.m_seenAt < K_SETTLE_MS) return null;
    if (this.m_tree && this.m_treeInstances === instances) {
      this.m_tree.refit();
    } else {
      this.m_tree = new PaddedPartTree(this.m_batch, { verbose: false });
    }
    this.m_treeVersion = version;
    this.m_treeInstances = instances;
    return this.m_tree;
  }
}

/**
 * Bumped by every `setMatrixAt` (it sets the texture's `needsUpdate`). Null if
 * a three upgrade renames the field: the caller then tests every instance.
 */
function instanceMatricesVersion(batch: BatchedMesh): number | null {
  const texture = (batch as unknown as { _matricesTexture?: { version?: number } | null })._matricesTexture;
  return typeof texture?.version === "number" ? texture.version : null;
}
